import * as React from "react";
import Word from "@/components/Word";
import SurahBeginning from "@/components/SurahBeginning";
import DialogAyah from "@/components/DialogAyah";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toArabicDigits } from "@/lib/utils";
import { useColorsSettings } from "@/store/settings";
import { mushafTextClass, sortLineWords, surahStartingOnLine } from "@/utils";
import type { Word as FetchedWord, AyahWithWords } from "@/lib/types";

interface QuranPageReadingViewProps {
  lines: FetchedWord[][];
  editMode: boolean;
  pageNumber: number;
  ayahs: AyahWithWords[];
}

const QuranPageReadingView: React.FC<QuranPageReadingViewProps> = ({
  lines,
  editMode,
  pageNumber,
  ayahs,
}) => {
  // One dialog for the whole page (words are plain buttons), so a page turn
  // renders ~150 light buttons instead of ~150 Radix dialogs. The ayah id
  // outlives `open` so the content stays put while the exit animation runs.
  const [open, setOpen] = React.useState(false);
  const [ayahId, setAyahId] = React.useState<number | null>(null);
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const articleRef = React.useRef<HTMLElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);

  // Read once here; words are memoised, so other settings changes (the last
  // page is saved after every turn) stop at this component.
  const { colors } = useColorsSettings();
  const opening = pageNumber <= 2;

  const pageLines = React.useMemo(
    () =>
      lines.map((line) => {
        const words = sortLineWords(line);
        return { words, surahStart: surahStartingOnLine(words) };
      }),
    [lines],
  );

  const verseKeys = React.useMemo(
    () => new Map(ayahs.map((ayah) => [ayah.id, ayah.verse_key])),
    [ayahs],
  );

  const selectedAyah = React.useMemo(
    () => (ayahId == null ? undefined : ayahs.find((ayah) => ayah.id === ayahId)),
    [ayahs, ayahId],
  );

  const handleSelect = React.useCallback((id: number, trigger: HTMLElement) => {
    triggerRef.current = trigger;
    setAyahId(id);
    setOpen(true);
  }, []);

  // Focus goes back to the word that opened the dialog, or to the first word
  // of the ayah the reader navigated to inside it.
  const handleCloseAutoFocus = React.useCallback(
    (event: Event) => {
      event.preventDefault();
      const trigger = triggerRef.current;
      const target =
        trigger?.isConnected && trigger.dataset.ayahId === String(ayahId)
          ? trigger
          : (articleRef.current?.querySelector<HTMLElement>(`[data-ayah-id="${ayahId}"]`) ??
            trigger);
      target?.focus({ preventScroll: true });
    },
    [ayahId],
  );

  return (
    <article
      ref={articleRef}
      aria-label={`Stranica ${pageNumber}`}
      className="mushaf-page relative mx-auto w-fit bg-paper-100 md:rounded-[6px] md:px-10 md:pb-7 md:pt-9 md:shadow-page md:ring-1 md:ring-paper-300 print:ring-0"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-2.5 hidden rounded-[3px] ring-1 ring-gold-500/35 md:block print:hidden"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-3.5 hidden rounded-[2px] ring-1 ring-gold-500/15 md:block print:hidden"
      />

      <div
        dir="rtl"
        className={`${mushafTextClass(pageNumber)} text-ink`}
        style={{ fontFamily: `p${pageNumber}` }}
      >
        {pageLines.map(({ words, surahStart }) => (
          <React.Fragment key={words[0].id}>
            {surahStart != null && <SurahBeginning number={surahStart} compact={opening} />}
            <div className="whitespace-nowrap text-center">
              {words.map((word) => (
                <Word
                  key={word.id}
                  word={word}
                  editMode={editMode}
                  showColors={colors}
                  verseKey={verseKeys.get(word.ayah_id)}
                  onSelect={handleSelect}
                />
              ))}
            </div>
          </React.Fragment>
        ))}
      </div>

      <footer aria-hidden className="mt-6 flex items-center justify-center gap-3">
        <span className="h-px w-10 bg-gradient-to-r from-transparent to-gold-500/60" />
        <span lang="ar" className="font-arabic text-xl leading-none text-gold-700">
          {toArabicDigits(pageNumber)}
        </span>
        <span className="h-px w-10 bg-gradient-to-l from-transparent to-gold-500/60" />
      </footer>

      {!editMode && (
        <Dialog open={open && selectedAyah != null} onOpenChange={setOpen}>
          {selectedAyah && (
            <DialogContent
              ref={contentRef}
              className="flex max-h-[calc(100svh-2rem)] max-w-2xl flex-col gap-0 overflow-hidden p-0"
              // Focus the dialog itself rather than the first switch; Radix
              // still traps focus inside it.
              onOpenAutoFocus={(event) => {
                event.preventDefault();
                contentRef.current?.focus();
              }}
              onCloseAutoFocus={handleCloseAutoFocus}
            >
              <DialogAyah
                ayah={selectedAyah}
                ayahs={ayahs}
                lines={lines}
                onNavigate={setAyahId}
              />
            </DialogContent>
          )}
        </Dialog>
      )}

      {/* Page-turn shading (styles.css); must stay the article's last child. */}
      <div className="mushaf-page-shade" aria-hidden />
    </article>
  );
};

export default React.memo(QuranPageReadingView);
